import { Eyebrow, Pill } from '@/components/ui';
import { CheckIcon, GridIcon, SparkIcon, SyncIcon, TrendUpIcon, TimerIcon } from '@/lib/icons';

const MODULES = [
  {
    name: 'Summary',
    body: 'Clients — total, active, missed and new — whose programme review is due, revenue, and the coach’s own week. The first screen is the answer to “what needs me today?”, not a dashboard of vanity metrics.',
    icon: <TrendUpIcon className="text-lg" />,
    premium: false,
  },
  {
    name: 'Calendar & bookings',
    body: 'A schedule view where the coach defines availability slots and marks each as individual or shared/group. Clients book into them, and capacity is enforced rather than displayed.',
    icon: <TimerIcon className="text-lg" />,
    premium: false,
  },
  {
    name: 'Clients & programmes',
    body: 'Each client opens their own programme and grid — a minimal version of the lifter interface — which the coach can read, edit and prescribe into. Their compliance, adherence, PRs and flags sit alongside.',
    icon: <GridIcon className="text-lg" />,
    premium: false,
  },
  {
    name: 'Payments & revenue',
    body: 'Who has paid, who is due, and revenue by period and by coach.',
    icon: <CheckIcon className="text-lg" />,
    premium: false,
  },
  {
    name: 'AI assistance',
    body: 'Which clients are at risk of churning, who is stalling, who has pain flags, and what to do next — the same evaluation matrix that powers the lifter’s AI coach, read across a roster.',
    icon: <SparkIcon className="text-lg" />,
    premium: true,
  },
  {
    name: 'Chat',
    body: 'Message a client from their summary. It starts as a simple thread; meetings are a later concern.',
    icon: <SyncIcon className="text-lg" />,
    premium: false,
  },
];

const CAPACITY_RULES = [
  'When a coach creates a slot they set its capacity — 1 for a one-to-one, N for a shared or group session — and that number enforces the limit.',
  'The slot is full once booked == capacity, and a further booking is refused with a clear message and a waitlist or “join the next” affordance rather than a dead end.',
  'Capacity can be raised or lowered later: raising opens places; lowering below the number already booked is refused.',
  'A cancellation frees a place immediately, and the slot is bookable again.',
  'Individual slots (capacity 1) never accept a second client, including via a race — enforced in the database, not only in the API.',
  'The coach sees booked / capacity per slot at a glance, and which client holds each place.',
  'A client cannot hold two places in the same slot, and the coach cannot book a client who is not linked to them.',
];

/**
 * The CRM product, straight from platform plan §4. Nothing here is invented:
 * summary, calendar & bookings with slot capacity, clients and programmes,
 * payments and revenue, AI assistance (the premium layer) and chat.
 */
export function CrmProduct() {
  return (
    <section id="crm-product" className="border-b border-hairline bg-page py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="mb-10 flex flex-col gap-4">
          <Eyebrow>The product</Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
            Everything a coach runs, in one workspace.
          </h2>
          <p className="max-w-[76ch] text-body-lg text-ink-muted">
            The CRM is deliberately scoped: booking and availability, not a full venue-management suite. Each
            client opens their own programme and grid; the coach reads, edits and prescribes. AI assistance is
            the premium layer, priced inside the tiers rather than sold separately.
          </p>
        </div>

        <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((module) => (
            <li
              key={module.name}
              className="flex flex-col gap-4 rounded-card border border-hairline bg-surface p-6 shadow-card"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center bg-brand-charcoal text-brand-white">
                  {module.icon}
                </span>
                {module.premium ? <Pill tone="accent">Premium layer</Pill> : null}
              </div>
              <h3 className="font-display text-[17px] font-black uppercase tracking-[-0.01em] text-ink">
                {module.name}
              </h3>
              <p className="text-[13px] leading-relaxed text-ink-muted">{module.body}</p>
            </li>
          ))}
        </ul>

        <div className="mt-10 rounded-card border border-hairline bg-surface p-7 shadow-card">
          <div className="flex flex-col gap-3">
            <Eyebrow>Slot capacity is a rule, not a label</Eyebrow>
            <h3 className="font-display text-[22px] font-black uppercase tracking-[-0.01em] text-ink">
              Capacity enforces the limit.
            </h3>
            <p className="max-w-[80ch] text-[14px] leading-relaxed text-ink-muted">
              A shared or group session is not a label on a calendar entry — the number the coach sets is the
              number of places that exist.
            </p>
          </div>

          <ul className="mt-6 grid gap-4 md:grid-cols-2">
            {CAPACITY_RULES.map((rule) => (
              <li key={rule} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-ink">
                <CheckIcon className="mt-[2px] shrink-0 text-sm text-accent" />
                {rule}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
